import React, { useState, useEffect } from 'react';
import { fetchProducts, fetchTanks, fetchPumps, savePumpReading, fetchPumpReadings, fetchTankReadings, fetchNozzles, saveNozzleReading, fetchNozzleReadings, saveTankReading, saveDipReading, fetchDipReadings } from '../../services/api';
import '../Entry/Entry.css';

const formatDateForAPI = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("-") && dateStr.split("-")[0].length === 4) {
    const [y, m, d] = dateStr.split("-");
    return `${d}-${m}-${y}`;
  }
  return dateStr;
};

const formatDateForUI = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("-") && dateStr.split("-")[0].length <= 2) {
    const [d, m, y] = dateStr.split("-");
    return `${y}-${m}-${d}`;
  }
  return dateStr;
};

const getTodayForUI = () => {
  const d = new Date();
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  let year = d.getFullYear();
  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;
  return [year, month, day].join('-');
};

export default function ClosingStock({ user }) {
  const companyId = user?.Companyid || user?.company_id || "1";

  const [activeTab, setActiveTab] = useState('tank');
  const [entries, setEntries] = useState([]);

  const [tanks, setTanks] = useState([]);
  const [pumps, setPumps] = useState([]);
  const [nozzles, setNozzles] = useState([]);
  const [products, setProducts] = useState([]);

  const [form, setForm] = useState({
    date: getTodayForUI(),
    product: '',
    tank: '',
    reading: '',
    shift: 'A',
    status: 'Active',
    stock: 'Closing'
  });

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  async function loadTankReadings() {
    try {
      const result = await fetchTankReadings(companyId);
      if (result.success && result.Response) {
        const fetchEntries = result.Response.map(item => ({
          id: `tank_val_${item.Tankid}_${item.SNo}`,
          dbId: item.Tankid,
          sno: item.SNo,
          tab: 'tank',
          product: item.ProductName,
          tank: item.TankName,
          reading: item.ClosingReading || item.OpeningReading,
          openingReading: item.OpeningReading,
          closingReading: item.ClosingReading,
          balance: item.Balance,
          date: item.Date,
          status: item.Status
        }));
        setEntries(prev => [...prev.filter(e => e.tab !== 'tank'), ...fetchEntries]);
      }
    } catch (err) { console.error(err); }
  }

  async function loadPumpReadings() {
    try {
      const result = await fetchPumpReadings(companyId);
      if (result.success && result.Response) {
        const fetchEntries = result.Response.map(item => ({
          id: `pump_val_${item.Pumpid}_${item.SNo}`,
          dbId: item.Pumpid,
          sno: item.SNo,
          tab: 'pump',
          product: item.ProductName,
          tank: item.PumpName,
          reading: item.ClosingReading || item.OpeningReading,
          openingReading: item.OpeningReading,
          shift: item.Shift,
          stock: item.Type,
          date: item.Date,
          status: item.Status
        }));
        setEntries(prev => [...prev.filter(e => e.tab !== 'pump'), ...fetchEntries]);
      }
    } catch (err) { console.error(err); }
  }

  async function loadNozzleReadings() {
    try {
      const result = await fetchNozzleReadings(companyId);
      if (result.success && result.Response) {
        const fetchEntries = result.Response.map(item => ({
          id: `nozzle_val_${item.Nozzleid}_${item.SNo}`,
          dbId: item.Nozzleid,
          sno: item.SNo,
          tab: 'nozzle',
          product: item.ProductName,
          tank: item.NozzleName,
          reading: item.ClosingReading || item.OpeningReading,
          openingReading: item.OpeningReading,
          shift: item.Shift,
          stock: item.Type,
          date: item.Date,
          status: item.Status
        }));
        setEntries(prev => [...prev.filter(e => e.tab !== 'nozzle'), ...fetchEntries]);
      }
    } catch (err) { console.error(err); }
  }

  async function loadDipReadings() {
    try {
      const result = await fetchDipReadings(companyId);
      if (result.success && result.Response) {
        const fetchEntries = result.Response.map(item => ({
          id: `dip_val_${item.Dipid}_${item.SNo}`,
          dbId: item.Dipid,
          sno: item.SNo,
          tab: 'dip',
          product: item.ProductName,
          tank: item.TankName,
          reading: item.ClosingReading || item.OpeningReading,
          openingReading: item.OpeningReading,
          shift: item.Shift,
          stock: item.Type,
          date: item.Date,
          status: item.Status
        }));
        setEntries(prev => [...prev.filter(e => e.tab !== 'dip'), ...fetchEntries]);
      }
    } catch (err) { console.error(err); }
  }

  async function loadInitialData() {
    const [prodRes, tankRes, pumpRes, nozzleRes] = await Promise.all([
      fetchProducts(companyId), fetchTanks(companyId), fetchPumps(companyId), fetchNozzles(companyId)
    ]);
    if (prodRes.success) setProducts(prodRes.Response || []);
    if (tankRes.success) setTanks(tankRes.Response || []);
    if (pumpRes.success) setPumps(pumpRes.Response || []);
    if (nozzleRes.success) setNozzles(nozzleRes.Response || []);
    loadTankReadings(); loadPumpReadings(); loadNozzleReadings(); loadDipReadings();
  }

  useEffect(() => { loadInitialData(); }, [companyId]);

  const handleSave = async () => {
    if (form.reading === "") return;
    try {
      let result;
      const commonPayload = {
        Companyid: companyId,
        Product: form.product,
        Reading: form.reading,
        Type: "Closing",
        Status: form.status,
        Date1: formatDateForAPI(form.date),
        Shift: form.shift,
        IDS: editId ? String(editId) : "0"
      };

      if (activeTab === "tank") {
        result = await saveTankReading({ ...commonPayload, TankName: form.tank });
        if (result.success) loadTankReadings();
      } else if (activeTab === "pump") {
        result = await savePumpReading({ ...commonPayload, PumpName: form.tank });
        if (result.success) loadPumpReadings();
      } else if (activeTab === "nozzle") {
        result = await saveNozzleReading({ ...commonPayload, NozzleName: form.tank });
        if (result.success) loadNozzleReadings();
      } else if (activeTab === "dip") {
        result = await saveDipReading({ ...commonPayload, TankName: form.tank });
        if (result.success) loadDipReadings();
      }

      if (result?.success || result?.Response === "UPDATED" || result?.Response === "INSERTED" || result?.Response === "ADDED") {
        setShowForm(false);
        setEditId(null);
        setForm({ ...form, reading: "" });
      } else {
        alert(result?.message || "Save failed");
      }
    } catch (err) { console.error(err); alert("Something went wrong"); }
  };

  function handleEdit(entry) {
    setEditId(entry.dbId || entry.sno);
    setForm({
      date: formatDateForUI(entry.date),
      product: entry.product,
      tank: entry.tank,
      reading: entry.closingReading || entry.reading,
      shift: entry.shift || 'A',
      status: entry.status,
      stock: 'Closing'
    });
    setShowForm(true);
  }

  return (
    <div className="entry-page">
      <div className="page-header">
        <h2 className="page-title">Closing Readings</h2>
        <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(true); setEditId(null); setForm({ ...form, reading: '', stock: 'Closing' }); }}>＋ New Entry</button>
      </div>

      <div className="tabs">
        {['tank', 'pump', 'nozzle', 'dip'].map(tab => (
          <button key={tab} className={`tab-btn ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {showForm && (
        <div className="section-card">
          <div className="section-header">{activeTab.toUpperCase()} Closing Entry</div>
          <div className="form-row">
            <div className="form-group">
              <label>Product</label>
              <select className="form-control" value={form.product} onChange={e => setForm({ ...form, product: e.target.value })}>
                <option value="">Select Product</option>
                {products.map(p => <option key={p.Productid} value={p.Name}>{p.Name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{activeTab === 'pump' ? 'Pump' : (activeTab === 'nozzle' ? 'Nozzle' : 'Tank')}</label>
              <select className="form-control" value={form.tank} onChange={e => {
                const val = e.target.value;
                let prod = form.product;
                if (activeTab === 'nozzle') { const nz = nozzles.find(n => n.NozzleName === val); if (nz) prod = nz.ProductName || prod; }
                else if (activeTab === 'tank') { const tk = tanks.find(t => t.TankName === val); if (tk) prod = tk.ProductName || prod; }
                else if (activeTab === 'pump') { const pm = pumps.find(p => p.PumpName === val); if (pm) prod = pm.ProductName || prod; }
                setForm({ ...form, tank: val, product: prod });
              }}>
                <option value="">Select {activeTab === 'pump' ? 'Pump' : (activeTab === 'nozzle' ? 'Nozzle' : 'Tank')}</option>
                {(activeTab === 'pump' ? pumps : activeTab === 'nozzle' ? nozzles : tanks).map(item => (
                  <option key={item.Pumpid || item.Nozzleid || item.Tankid} value={item.PumpName || item.NozzleName || item.TankName}>
                    {item.PumpName || item.NozzleName || item.TankName}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group"><label>Closing Reading</label><input type="number" className="form-control" value={form.reading} onChange={e => setForm({ ...form, reading: e.target.value })} /></div>
            <div className="form-group">
              <label>Shift</label>
              <select className="form-control" value={form.shift} onChange={e => setForm({ ...form, shift: e.target.value })}><option value="A">A</option><option value="B">B</option><option value="C">C</option></select>
            </div>
            <div className="form-group"><label>Date</label><input type="date" className="form-control" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div className="form-group">
              <label>Status</label>
              <select className="form-control" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}><option>Active</option><option>Inactive</option></select>
            </div>
          </div>
          <div className="form-row" style={{ paddingTop: 0 }}><div style={{ flex: 1 }} /><div className="action-bar"><button className="btn btn-primary" onClick={handleSave}><i className="bi bi-save me-1"></i> Save Closing</button><button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ Cancel</button></div></div>
        </div>
      )}

      <div className="section-card">
        <div className="section-header">{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Closing Entries</div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-left">Product</th>
                <th className="text-left">{activeTab === 'pump' ? 'Pump' : (activeTab === 'nozzle' ? 'Nozzle' : 'Tank')}</th>
                {activeTab === 'tank' ? (
                  <>
                    <th className="text-right">Opening Reading</th>
                    <th className="text-right">Closing Reading</th>
                    <th className="text-right">Balance</th>
                  </>
                ) : (
                  <>
                    <th className="text-right">Closing Reading</th>
                    <th className="text-center">Shift</th>
                  </>
                )}
                <th className="text-center">Date</th>
                <th className="text-center">Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {entries.filter(e => e.tab === activeTab).map(e => (
                <tr key={e.id}>
                  <td className="text-left"><span className="badge badge-info">{e.product}</span></td>
                  <td className="text-left">{e.tank}</td>
                  {activeTab === 'tank' ? (
                    <>
                      <td className="text-right">{parseFloat(e.openingReading || 0).toFixed(3)}</td>
                      <td className="text-right">{e.closingReading ? parseFloat(e.closingReading).toFixed(3) : '-'}</td>
                      <td className="text-right" style={{ fontWeight: 700 }}>{e.balance ? parseFloat(e.balance).toFixed(3) : '-'}</td>
                    </>
                  ) : (
                    <>
                      <td className="text-right">{parseFloat(e.reading || 0).toFixed(3)}</td>
                      <td className="text-center">{e.shift}</td>
                    </>
                  )}
                  <td className="text-center">{e.date}</td>
                  <td className="text-center"><span className={`badge ${e.status === 'Active' ? 'badge-success' : 'badge-warning'}`}>{e.status}</span></td>
                  <td className="text-center"><button className="icon-btn" onClick={() => handleEdit(e)}><i className="bi bi-pencil-square"></i></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}